import { useMemo } from 'react';
import { useT } from '../i18n';
import { LANGS, LANG_LABELS, LANG_SHORT, type Lang } from '../locales';
import { Dropdown, type DropdownOption } from './Dropdown';

/**
 * Vlaječka země k jazyku. Kreslená inline (SVG), NE emoji – vlajkové emoji
 * se na Windows/Chrome nevykreslují (chybí glyfy), takže by z nich zbyla jen
 * písmena. Zjednodušené (SK/DE/PL jsou pruhy, CZ pruhy + klín), na 20×14 to
 * stačí a je to čitelné i v malém.
 */
function FlagIcon({ lang }: { lang: Lang }) {
  const common = {
    width: 20,
    height: 14,
    viewBox: '0 0 20 14',
    className: 'flag',
    'aria-hidden': true,
    focusable: false,
  } as const;

  switch (lang) {
    case 'cs':
      return (
        <svg {...common}>
          <rect width="20" height="7" fill="#fff" />
          <rect y="7" width="20" height="7" fill="#d7141a" />
          <path d="M0 0 10 7 0 14Z" fill="#11457e" />
        </svg>
      );
    case 'sk':
      return (
        <svg {...common}>
          <rect width="20" height="4.67" fill="#fff" />
          <rect y="4.67" width="20" height="4.67" fill="#0b4ea2" />
          <rect y="9.33" width="20" height="4.67" fill="#ee1620" />
        </svg>
      );
    case 'de':
      return (
        <svg {...common}>
          <rect width="20" height="4.67" fill="#000" />
          <rect y="4.67" width="20" height="4.67" fill="#dd0000" />
          <rect y="9.33" width="20" height="4.67" fill="#ffce00" />
        </svg>
      );
    case 'pl':
      return (
        <svg {...common}>
          <rect width="20" height="7" fill="#fff" />
          <rect y="7" width="20" height="7" fill="#dc143c" />
        </svg>
      );
    default:
      return null;
  }
}

/** Přepínač jazyka výlohy (CZ/SK/DE/PL). Volba se pamatuje v prohlížeči. */
export function LanguageSwitcher() {
  const { lang, setLang, t } = useT();
  const options = useMemo<DropdownOption<Lang>[]>(
    () =>
      LANGS.map((code) => ({
        value: code,
        label: LANG_LABELS[code],
        short: LANG_SHORT[code],
        icon: <FlagIcon lang={code} />,
      })),
    [],
  );
  return (
    <Dropdown
      className="dropdown--lang"
      ariaLabel={t('lang.switch')}
      value={lang}
      options={options}
      onChange={setLang}
    />
  );
}
